import React, { useEffect, useState, useCallback } from 'react';
import { Clock, CheckCircle, XCircle, RefreshCw, CalendarCheck } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { apiClient, ScheduledQueriesSummaryResponse } from '@/utils/apiClient';

interface ScheduledQuerySummaryProps {
  className?: string;
}

export const ScheduledQuerySummary: React.FC<ScheduledQuerySummaryProps> = ({ className }) => {
  const { t, language } = useTranslation();
  const counts = t.analytics.counts;
  const at = (t as any).analytics;
  const [data, setData] = useState<ScheduledQueriesSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getScheduledQueriesSummary();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [at?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const totalRunsWeek = data.total_runs_this_week;
  const successRate = totalRunsWeek > 0
    ? ((data.success_count / totalRunsWeek) * 100).toFixed(0)
    : '—';

  const successRateColor =
    totalRunsWeek === 0
      ? 'text-gray-500'
      : Number(successRate) >= 90
      ? 'text-green-600 dark:text-green-400'
      : Number(successRate) >= 70
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-red-600 dark:text-red-400';

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
            <CalendarCheck className="h-5 w-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.scheduledSummary ?? 'Scheduled Summary'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatCount(language, data.total_schedules, counts.schedules)} · {formatCount(language, data.active_schedules, counts.active)}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
        >
          <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
          <Clock className="h-3.5 w-3.5 mx-auto text-blue-500 mb-1" />
          <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{data.active_schedules}</p>
          <p className="text-[9px] text-gray-500 dark:text-gray-400">{at?.activeSchedules ?? 'Active'}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
          <CheckCircle className="h-3.5 w-3.5 mx-auto text-green-500 mb-1" />
          <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{totalRunsWeek}</p>
          <p className="text-[9px] text-gray-500 dark:text-gray-400">{at?.runsThisWeek ?? 'Runs/wk'}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
          <XCircle className="h-3.5 w-3.5 mx-auto text-red-500 mb-1" />
          <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{data.failure_count}</p>
          <p className="text-[9px] text-gray-500 dark:text-gray-400">{at?.failures ?? 'Failures'}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
          <CalendarCheck className="h-3.5 w-3.5 mx-auto text-teal-500 mb-1" />
          <p className={clsx('text-lg font-bold tabular-nums', successRateColor)}>
            {successRate}{totalRunsWeek > 0 ? '%' : ''}
          </p>
          <p className="text-[9px] text-gray-500 dark:text-gray-400">{at?.successRate ?? 'Success'}</p>
        </div>
      </div>

      {/* Compact inline summary */}
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        {formatCount(language, data.active_schedules, counts.activeSchedules)} · {formatCount(language, totalRunsWeek, counts.runsThisWeek)} · {formatCount(language, data.failure_count, counts.failures)} · {formatCount(language, data.total_runs_all_time, counts.totalRuns, data.total_runs_all_time.toLocaleString())}
      </div>
    </div>
  );
};
